import React from 'react';
import { useDropzone, FileRejection } from 'react-dropzone';
import styles from './DropZoneInput.module.css';
import Spacer from '../Spacer';
import FilledAddIcon from '../../../assets/icons/FilledAddIcon';
import FilledCross from '../../../assets/icons/FilledCross';

interface Props {
  label?: string;
  required?: boolean;
  onDrop: (acceptedFiles: any[]) => void;
  onDropRejected?: (e: FileRejection[]) => void;
  selectedFiles: any[];
  onRemove: (index: number) => void;
  error?: string;
  disabled?: boolean;
}

interface FullSizeDropZoneProps {
  isDragActive: boolean;
  children: React.ReactNode;
}

const FullSizeDropZone = React.forwardRef<HTMLDivElement, FullSizeDropZoneProps>(function FullSizeDropZone(
  { children, isDragActive, ...rest },
  ref
) {
  return (
    <div {...rest} className={styles.fullSizeDropZone} ref={ref} style={{ borderColor: isDragActive ? '#15803D' : '' }}>
      <div className={styles.dropZoneAddContainer}>
        <FilledAddIcon />
        <span>Add here</span>
      </div>
      {children}
    </div>
  );
});

interface PreviewAndDropzoneProps {
  children: React.ReactNode;
  selectedFiles: any[];
  onRemove: (index: number) => void;
  isDragActive: boolean;
}

const PreviewAndDropzone = React.forwardRef<HTMLDivElement, PreviewAndDropzoneProps>(function PreviewAndDropzone({
  isDragActive,
  children,
  onRemove,
  selectedFiles,
  ...rest
}) {
  return (
    <div className={styles.dropZonePreviewContainer}>
      {selectedFiles.map(({ preview }, index) => (
        <div className={styles.dropZoneImageCard} key={preview}>
          <FilledCross className={styles.removeImageIcon} onClick={() => onRemove(index)} />
          <img src={preview} className={styles.dropZoneImage} alt="story" />
        </div>
      ))}
      {selectedFiles.length < 4 && (
        <div {...rest} className={styles.cardDropZone} style={{ borderColor: isDragActive ? '#15803D' : '' }}>
          <FilledAddIcon />
          <span className={styles.cardDropZoneText}>
            Drop here or <span style={{ color: '#7F38E9' }}>browse</span>
          </span>
          {children}
        </div>
      )}
    </div>
  );
});

function DropZoneInput({
  label,
  required = false,
  onDrop,
  onDropRejected,
  selectedFiles,
  onRemove,
  error,
  disabled = false,
}: Props) {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    disabled,
    onDrop,
    accept: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpeg', '.jpg'],
    },
    multiple: true,
    maxFiles: 4,
    onDropRejected,
  });
  return (
    <div className={`${styles.dropZoneContainer} ${disabled ? styles.dropZoneContainerDisabled : ''}`}>
      <div className={styles.label}>
        {label}
        {required && <span className={styles.redAsterisk}> *</span>}
      </div>
      <Spacer spacing="8px" />
      {selectedFiles.length === 0 ? (
        <FullSizeDropZone {...getRootProps()} isDragActive={isDragActive}>
          <input {...getInputProps()} />
        </FullSizeDropZone>
      ) : (
        <PreviewAndDropzone
          {...getRootProps()}
          onRemove={onRemove}
          selectedFiles={selectedFiles}
          isDragActive={isDragActive}
        >
          <input {...getInputProps()} />
        </PreviewAndDropzone>
      )}
      {error && <div className={styles.dropZoneError}>{error}</div>}
    </div>
  );
}

export default DropZoneInput;
